// Hợp đồng của booking-service (đặc tả docs/backend/services/booking-service.md + docs/backend/sagas.md §9):
// trạng thái giữ căn, hàng chờ, cửa sổ ưu tiên, khoá căn chưa mở bán, bên tham gia saga giao dịch (deal điều phối),
// saga xoá (inventory điều phối) và saga nghỉ việc (hr điều phối).
// Quy ước: tiền VND số nguyên (number); thời điểm ISO 8601 UTC; id UUID. Mọi sự kiện booking.unit.* mang `snapshot`
// có `version` để inventory/crm/notification cập nhật mirror (chỉ áp version mới hơn). Khách gắn căn không bao giờ có
// trong snapshot (R25).
import { z } from 'zod';
import type { ProjectKit, UnitAttrs } from '../inventory/index.js';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const uuidReq = (required: string) => str(required).uuid('Mã định danh không hợp lệ');
const num = () => z.number({ required_error: REQUIRED, invalid_type_error: 'Phải là số' });
const money = num().int('Số tiền phải là số nguyên').min(0, 'Số tiền không được âm').max(9e15, 'Số tiền vượt giới hạn');
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};
/** ?projectIds=a,b hoặc mảng ⇒ mảng. */
const splitList = (v: unknown) => {
  if (v === undefined || v === null || v === '') return undefined;
  return (Array.isArray(v) ? v.flatMap((x) => String(x).split(',')) : String(v).split(',')).map((s) => s.trim()).filter(Boolean);
};

// ---------- Hằng số ----------
/** Trạng thái lưu (raw) của UnitReservation; withdrawn = tombstone khi căn bị xoá. */
export const RESERVATION_STATUSES = ['open', 'locked', 'pending', 'deposited', 'contracted', 'blocked', 'withdrawn'] as const;
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];
/** Trạng thái hiệu lực (effectiveUnit): giữ quá hạn ⇒ open. Nhãn ở packages/domain UNIT_STATUS. */
export const EFFECTIVE_STATUSES = ['open', 'locked', 'pending', 'deposited', 'contracted', 'blocked'] as const;
export type EffectiveStatus = (typeof EFFECTIVE_STATUSES)[number];
export const RELEASE_REASONS = ['holder', 'override', 'expired', 'deal_rejected', 'deal_cancelled', 'unit_changed', 'unblocked', 'offboarding', 'project_withdrawn'] as const;
export type ReleaseReason = (typeof RELEASE_REASONS)[number];
export const LOCK_END_REASONS = ['released', 'override_released', 'expired', 'reserved_for_deal', 'swapped_into_deal', 'offboarding', 'project_withdrawn'] as const;
export type LockEndReason = (typeof LOCK_END_REASONS)[number];
/** Lý do từ chối giữ chỗ cho phiếu cọc (SG-1 T1) — thống nhất với deal-service. */
export const RESERVATION_REJECT_REASONS = ['UNIT_NOT_LOCKED', 'UNIT_HELD_BY_OTHER', 'LOCK_EXPIRED', 'UNIT_NOT_FOUND', 'CANCELLED'] as const;
export type ReservationRejectReason = (typeof RESERVATION_REJECT_REASONS)[number];
/** Lý do từ chối hoán đổi căn (SG-6 U1). */
export const UNIT_CHANGE_REJECT_REASONS = ['TARGET_UNIT_NOT_AVAILABLE', 'DEAL_UNIT_NOT_LINKED', 'UNIT_NOT_FOUND', 'CANCELLED'] as const;
export type UnitChangeRejectReason = (typeof UNIT_CHANGE_REJECT_REASONS)[number];
export const UNIT_WITHDRAWAL_REJECT_REASONS = ['UNIT_NOT_OPEN', 'UNIT_HAS_DEAL'] as const;
export type UnitWithdrawalRejectReason = (typeof UNIT_WITHDRAWAL_REJECT_REASONS)[number];
/** Thông điệp giữ nguyên câu chữ prototype (FE hiển thị khi saga thất bại). */
export const REJECT_MESSAGES: Record<ReservationRejectReason | UnitChangeRejectReason | UnitWithdrawalRejectReason | 'PROJECT_HAS_DEALS', string> = {
  UNIT_NOT_LOCKED: 'Căn không còn ở trạng thái đang giữ',
  UNIT_HELD_BY_OTHER: 'Căn đang được người khác giữ',
  LOCK_EXPIRED: 'Đã hết thời gian giữ căn. Giữ lại căn rồi gửi phiếu.',
  UNIT_NOT_FOUND: 'Căn không còn tồn tại',
  CANCELLED: 'Yêu cầu đã bị huỷ',
  TARGET_UNIT_NOT_AVAILABLE: 'Căn mới không còn trống',
  DEAL_UNIT_NOT_LINKED: 'Căn không gắn với giao dịch này',
  UNIT_NOT_OPEN: 'Chỉ xoá được căn đang trống, chưa có giao dịch',
  UNIT_HAS_DEAL: 'Chỉ xoá được căn đang trống, chưa có giao dịch',
  PROJECT_HAS_DEALS: 'Dự án đã có giao dịch, chỉ có thể chuyển sang "Đã đóng giỏ".',
};
/** Ngưỡng nhắc "Căn sắp hết thời gian giữ" (prototype alerts). */
export const LOCK_EXPIRING_MINUTES = 20;
/** `eventType` của booking.dealSync.anomalyDetected do đối soát đêm với deal GetDealUnitLinks phát (sagas.md §8) — không gắn sự kiện deal nào. */
export const DEAL_RECONCILE_EVENT_TYPE = 'booking.dealReconcile';

// ---------- Lệnh người dùng ----------
export const unitRef = z.object({ unitId: uuid });
/** Giữ căn (header Idempotency-Key). customerId tuỳ chọn — khách bắt buộc khi gửi phiếu cọc (deal). */
export const lockUnitInput = z.object({ unitId: uuid, customerId: uuid.nullish() });
export const setLockCustomerInput = z.object({ unitId: uuid, lockId: uuidReq('Thiếu lượt giữ căn'), customerId: uuidReq('Chọn khách hàng') });
/** lockId bắt buộc khi người thao tác không phải người giữ (mở khoá — R9). */
export const releaseUnitInput = z.object({ unitId: uuid, lockId: uuid.optional() });
export const extendLockInput = z.object({ unitId: uuid, lockId: uuidReq('Thiếu lượt giữ căn') });
export const joinQueueInput = unitRef;
export const leaveQueueInput = unitRef;
export const blockUnitInput = unitRef;
export const unblockUnitInput = unitRef;

// ---------- Truy vấn công khai ----------
export const listBoardProjectsInput = z.object({});
export const getBoardInput = z.object({ projectId: uuidReq('Chọn dự án') });
export const getUnitReservationInput = unitRef;
export const listMyLocksInput = z.object({});
export const listBoardActivityInput = z.object({ projectId: uuid, limit: qInt(1, 50, 8) });
export const getProjectBoardSummaryInput = z.object({ projectIds: z.preprocess(splitList, z.array(uuid).max(200, 'Tối đa 200 dự án').optional()) });
export const needsCriteria = z.object({
  budgetMin: money.default(0),
  budgetMax: money.default(0),
  unitType: text(60).nullish(),
  projectIds: z.array(uuid).max(50, 'Tối đa 50 dự án').default([]),
  area: num().positive('Diện tích phải lớn hơn 0').nullish(),
  direction: text(60).nullish(),
});
/** Gateway lấy nhu cầu từ crm GetCustomerNeeds (kiểm phạm vi customers.view) rồi gọi truy vấn này. */
export const matchUnitsForNeedsInput = z.object({
  needs: needsCriteria,
  projectId: uuid.optional(),
  limit: qInt(1, 40, 6),
  minScore: qInt(0, 100, 0),
});
/** Căn để đổi cho giao dịch: cùng dự án, ≠ căn hiện tại, hiệu lực open hoặc đang do sale của giao dịch giữ. */
export const listSwapCandidatesInput = z.object({ dealId: uuid });

// ---------- Truy vấn nội bộ ----------
export const listActiveLocksByStaffInput = z.object({ staffId: uuid });
export const getUnitReservationsInput = z.object({ unitIds: z.array(uuid).min(1, 'Chọn ít nhất một căn').max(200, 'Tối đa 200 căn') });
export const listReservationSnapshotsInput = z.object({ projectId: uuid.optional(), afterUnitId: uuid.optional(), limit: qInt(1, 1000, 500) });
export const listStaffBookingAlertsInput = z.object({ staffIds: z.array(uuid).max(1000, 'Tối đa 1.000 nhân sự').optional() });

/** Tên RPC: booking.cmd.<Name> / booking.qry.<Name>. */
export const RPC = {
  cmd: ['LockUnit', 'SetLockCustomer', 'ReleaseUnit', 'ExtendLock', 'JoinQueue', 'LeaveQueue', 'BlockUnit', 'UnblockUnit'],
  qry: [
    'ListBoardProjects',
    'GetBoard',
    'GetUnitReservation',
    'ListMyLocks',
    'ListBoardActivity',
    'GetProjectBoardSummary',
    'MatchUnitsForNeeds',
    'ListSwapCandidates',
    // nội bộ
    'ListActiveLocksByStaff',
    'GetUnitReservations',
    'ListReservationSnapshots',
    'ListStaffBookingAlerts',
  ],
} as const;

// ---------- DTO / read model ----------
/** Ảnh chụp trạng thái giữ chỗ — trong mọi sự kiện booking.unit.* (không có khách). */
export interface ReservationSnapshot {
  unitId: string;
  projectId: string;
  status: ReservationStatus;
  version: number;
  lockId: string | null;
  holderStaffId: string | null;
  lockedAt: string | null;
  lockUntil: string | null;
  extendedCount: number;
  dealId: string | null;
  /** Phiên bản giao dịch mới nhất đã áp lên căn (B-R3). */
  dealVersion: number | null;
  releasedAt: string | null;
  releaseReason: ReleaseReason | null;
  queueLength: number;
  queueHeadStaffId: string | null;
  /** Hết cửa sổ ưu tiên của người đầu hàng chờ (tính lúc phát). */
  priorityUntil: string | null;
}
/**
 * Một dòng của ListReservationSnapshots: snapshot + mã căn từ danh mục bảng hàng (board_unit_catalog) — notification dựng lại
 * unit_watch có mã. Payload sự kiện giữ nguyên ReservationSnapshot (mã căn nằm ở `code` của sự kiện). booking luôn điền.
 */
export type ReservationSnapshotItem = ReservationSnapshot & { code?: string };
export interface PriorityInfo {
  staffId: string;
  until: string;
}
export interface BookingSettingsView {
  lockMinutes: number;
  maxLocks: number;
  priorityMinutes: number;
  extendMinutes: number;
}
export interface BoardUnitDto {
  unitId: string;
  projectId: string;
  code: string;
  block: string;
  floor: number;
  pos: number | null;
  type: string;
  area: number | null;
  direction: string;
  view: string;
  price: number;
  pricePerM2: number;
  address: string | null;
  legalNote: string | null;
  consignedAt: string | null;
  /** Thuộc tính bảng hàng CĐT (trục, quỹ, giá theo phương án, tài liệu căn) — bản sao từ inventory; {} khi không có. */
  attrs: UnitAttrs;
  /** Trạng thái hiệu lực tại serverTime. */
  status: EffectiveStatus;
  rawStatus: ReservationStatus;
  version: number;
  lock: { lockId: string; holderStaffId: string; holderName: string; holderShortName: string; lockedAt: string; until: string; extendedCount: number } | null;
  priority: { staffId: string; staffName: string; until: string } | null;
  queue: { staffId: string; name: string }[];
  deal: { dealId: string; code: string; staffId: string; staffName: string; status: string; value: number; createdAt: string } | null;
}
export interface BoardProjectChip {
  id: string;
  name: string;
  market: 'primary' | 'secondary';
  status: 'upcoming' | 'selling' | 'paused' | 'closed';
  floorLabel: string;
  openCount: number;
  totalCount: number;
}
export type EffectiveCounts = Record<EffectiveStatus, number>;
export interface Board {
  project: { id: string; name: string; market: 'primary' | 'secondary'; status: 'upcoming' | 'selling' | 'paused' | 'closed'; floorLabel: string; blocks: string[] };
  settings: BookingSettingsView;
  myActiveLockCount: number;
  counts: EffectiveCounts;
  types: string[];
  /** Id luồng đẩy để nối lại (Last-Event-ID). */
  streamCursor: string | null;
  serverTime: string;
  units: BoardUnitDto[];
}
export interface LockBlocker {
  code: string;
  message: string;
}
export interface UnitReservationActions {
  canLock: boolean;
  lockBlocker: LockBlocker | null;
  canRelease: boolean;
  canOverrideRelease: boolean;
  canExtend: boolean;
  canSetCustomer: boolean;
  canJoinQueue: boolean;
  canLeaveQueue: boolean;
  inQueue: boolean;
  canBlock: boolean;
  canUnblock: boolean;
}
export interface UnitReservationDetail extends BoardUnitDto {
  /** Khách gắn căn — chỉ người giữ / booking.override (R25). */
  lockCustomer: { id: string; name: string } | null;
  queueDetail: { position: number; staffId: string; name: string; isMe: boolean }[];
  priorityDetail: { staffId: string; name: string; until: string; isMe: boolean } | null;
  actions: UnitReservationActions;
  serverTime: string;
}
export interface LockUnitResult {
  unitId: string;
  lockId: string;
  lockedAt: string;
  until: string;
  version: number;
  serverTime: string;
}
export interface SetLockCustomerResult {
  unitId: string;
  lockId: string;
  customerId: string;
  version: number;
}
export interface ReleaseUnitResult {
  unitId: string;
  releasedAt: string;
  reason: 'holder' | 'override';
  /** Người đầu hàng chờ được ưu tiên tới `until` (nếu có). */
  priority: PriorityInfo | null;
  version: number;
  serverTime: string;
}
export interface ExtendLockResult {
  unitId: string;
  lockId: string;
  until: string;
  extendedCount: number;
  version: number;
  serverTime: string;
}
/** Kết quả xếp / rời hàng chờ. changed = false khi rời hàng chờ mà không ở trong hàng (idempotent). */
export interface QueueResult {
  unitId: string;
  inQueue: boolean;
  position: number | null;
  queueLength: number;
  changed: boolean;
  version: number;
}
/** Kết quả khoá / mở bán căn. */
export interface UnitStatusResult {
  unitId: string;
  status: EffectiveStatus;
  version: number;
  serverTime: string;
}
/** GET /booking/board (gateway gộp): Board + dòng thông tin dự án từ inventory — null khi inventory không phản hồi / không có quyền. */
export interface BoardView extends Board {
  projectInfo: { developer: string | null; location: string | null; agencyRate: number; bookingAmount: number; hotBonus: number; kit: ProjectKit } | null;
}
export interface MyLock {
  unitId: string;
  code: string;
  projectId: string;
  projectName: string;
  customerId: string | null;
  customerName: string | null;
  lockId: string;
  lockedAt: string;
  until: string;
  /** ms còn lại tại serverTime. */
  lockLeft: number;
}
export interface MyLocks {
  items: MyLock[];
  serverTime: string;
}
export interface BoardActivity {
  at: string;
  kind: 'locked' | 'reopened' | 'deposit_submitted' | 'deposit_confirmed';
  unitId: string;
  code: string;
  staffName: string | null;
  dealValue: number | null;
}
export interface ProjectBoardSummary {
  projects: { projectId: string; name: string; counts: EffectiveCounts; total: number; sellable: number; sold: number; absorptionPct: number }[];
  tenant: { open: number; locked: number };
}
export interface UnitMatch {
  unit: BoardUnitDto;
  score: number;
  why: string[];
}
export interface SwapCandidate {
  unitId: string;
  code: string;
  type: string;
  area: number | null;
  price: number;
}
// Nội bộ
export interface ActiveLockItem {
  unitId: string;
  code: string;
  projectName: string;
  until: string;
}
export interface UnitReservationState {
  unitId: string;
  effectiveStatus: EffectiveStatus;
  holderStaffId: string | null;
  lockUntil: string | null;
  dealId: string | null;
  version: number;
}
export interface StaffBookingAlert {
  staffId: string;
  kind: 'lock_expiring' | 'priority';
  unitId: string;
  code: string;
  until: string;
}

// ---------- Sự kiện ----------
export const EVENTS = {
  unitLocked: 'booking.unit.locked',
  unitLockCustomerSet: 'booking.unit.lockCustomerSet',
  unitLockExtended: 'booking.unit.lockExtended',
  unitReleased: 'booking.unit.released',
  unitLockExpired: 'booking.unit.lockExpired',
  unitQueueJoined: 'booking.unit.queueJoined',
  unitQueueLeft: 'booking.unit.queueLeft',
  unitPriorityWindowClosed: 'booking.unit.priorityWindowClosed',
  unitBlocked: 'booking.unit.blocked',
  unitUnblocked: 'booking.unit.unblocked',
  // Bên tham gia saga giao dịch (deal điều phối — sagas.md §9)
  unitReservedForDeal: 'booking.unit.reservedForDeal',
  unitReservationRejected: 'booking.unitReservation.rejected',
  unitReservationReverted: 'booking.unit.reservationReverted',
  unitDeposited: 'booking.unit.deposited',
  unitContracted: 'booking.unit.contracted',
  unitReopened: 'booking.unit.reopened',
  unitChangeCompleted: 'booking.unitChange.completed',
  unitChangeRejected: 'booking.unitChange.rejected',
  unitSyncFailed: 'booking.unitSync.failed',
  dealSyncAnomalyDetected: 'booking.dealSync.anomalyDetected',
  // Phản hồi bước S3 của saga nghỉ việc (hr-service điều phối)
  staffLocksReleased: 'booking.staffLocks.released',
  // Bên tham gia saga xoá (inventory điều phối)
  unitWithdrawn: 'booking.unit.withdrawn',
  unitWithdrawalRejected: 'booking.unitWithdrawal.rejected',
  projectWithdrawn: 'booking.project.withdrawn',
  projectWithdrawalRejected: 'booking.projectWithdrawal.rejected',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

interface UnitEventBase {
  snapshot: ReservationSnapshot;
  code: string;
}
export interface UnitLocked extends UnitEventBase {
  staffId: string;
  customerId: string | null;
  lockId: string;
  lockedAt: string;
  until: string;
  lockMinutes: number;
  /** Giữ được nhờ đứng đầu hàng chờ trong cửa sổ ưu tiên. */
  viaPriority: boolean;
}
export interface UnitLockCustomerSet extends UnitEventBase {
  staffId: string;
  lockId: string;
  customerId: string;
  previousCustomerId: string | null;
}
export interface UnitLockExtended extends UnitEventBase {
  lockId: string;
  holderStaffId: string;
  until: string;
  extendedCount: number;
  extendedBy: string;
  extendMinutes: number;
}
export interface UnitReleased extends UnitEventBase {
  lockId: string;
  holderStaffId: string;
  releasedBy: string | null;
  reason: 'holder' | 'override' | 'offboarding' | 'project_withdrawn';
  releasedAt: string;
  offboardingId: string | null;
  priority: PriorityInfo | null;
}
export interface UnitLockExpired extends UnitEventBase {
  lockId: string;
  holderStaffId: string;
  /** = lock.until (không phải lúc job chạy — R6). */
  expiredAt: string;
  priority: PriorityInfo | null;
}
export interface UnitQueueJoined extends UnitEventBase {
  staffId: string;
  position: number;
  queueLength: number;
}
export interface UnitQueueLeft extends UnitEventBase {
  staffId: string;
  reason: 'self' | 'offboarding' | 'withdrawn';
  queueLength: number;
  /** Người rời đang được ưu tiên ⇒ người kế tiếp (nếu có) hưởng phần còn lại của cửa sổ. */
  priority: PriorityInfo | null;
}
export interface UnitPriorityWindowClosed extends UnitEventBase {
  staffId: string;
  freeAt: string;
  until: string;
}
export interface UnitBlocked extends UnitEventBase {
  blockedBy: string;
}
export interface UnitUnblocked extends UnitEventBase {
  unblockedBy: string;
  releasedAt: string;
  priority: PriorityInfo | null;
}
/** Phản hồi thành công SG-1 T1 (via = deposit) hoặc căn mới của SG-6 (via = unitChange). */
export interface UnitReservedForDeal extends UnitEventBase {
  dealId: string;
  dealVersion: number;
  via: 'deposit' | 'unitChange';
  sagaId: string;
  /** '<dealId>:reserve' (deposit) hoặc '<sagaId>:swap' (unitChange); '<dealId>:sync:<dealVersion>' khi xác nhận lại 'pending' cho deal.unitSync.requested. */
  idempotencyKey: string;
  unitId: string;
  projectId: string;
  staffId: string;
  customerId: string;
  /** Giá niêm yết đang hiển thị lúc giữ chỗ (deal chụp listPrice — D4). */
  listPrice: number;
  priceVersion: number;
  reservedAt: string;
}
export interface UnitReservationRejected {
  sagaId: string;
  idempotencyKey: string;
  dealId: string;
  unitId: string;
  reasonCode: ReservationRejectReason;
  message: string;
}
/** Bù trừ C1 của SG-1: khôi phục căn về lượt giữ cũ (hạn cũ; đã quá hạn ⇒ hiệu lực trống). */
export interface UnitReservationReverted extends UnitEventBase {
  sagaId: string;
  idempotencyKey: string;
  cancelKey: string;
  dealId: string;
  lockId: string | null;
  lockUntil: string | null;
  expired: boolean;
}
/** Áp sự kiện giao dịch sau pivot (B-R3); idempotencyKey có khi phản hồi deal.unitSync.requested. */
export interface UnitDeposited extends UnitEventBase {
  dealId: string;
  dealVersion: number;
  idempotencyKey: string | null;
}
export interface UnitContracted extends UnitEventBase {
  dealId: string;
  dealVersion: number;
  idempotencyKey: string | null;
}
export interface UnitReopened extends UnitEventBase {
  dealId: string;
  dealVersion: number;
  reason: 'deal_rejected' | 'deal_cancelled' | 'unit_changed';
  releasedAt: string;
  priority: PriorityInfo | null;
  idempotencyKey: string | null;
}
/** Phản hồi thành công SG-6 U1 (hoán đổi nguyên tử hai căn). Gửi lại khi nhận cancelRequested sau khi đã áp. */
export interface UnitChangeCompleted {
  sagaId: string;
  idempotencyKey: string; // '<sagaId>:swap'
  dealId: string;
  dealVersion: number;
  fromUnitId: string;
  toUnitId: string;
  toUnit: { projectId: string; code: string; listPrice: number; priceVersion: number };
}
export interface UnitChangeRejected {
  sagaId: string;
  idempotencyKey: string;
  dealId: string;
  fromUnitId: string;
  toUnitId: string;
  reasonCode: UnitChangeRejectReason;
  message: string;
}
/** Không tìm thấy căn gắn giao dịch khi cần deposited/contracted (hoặc lệch ở deal.unit.changed — B-R8). */
export interface UnitSyncFailed {
  dealId: string;
  dealVersion: number;
  unitId: string | null;
  expectedUnitStatus: 'pending' | 'deposited' | 'contracted' | 'open';
  reason: string;
  idempotencyKey: string | null;
}
export interface DealSyncAnomalyDetected {
  dealId: string;
  unitId: string;
  code: string | null;
  eventType: string;
  expected: { dealId: string | null; status: string };
  actual: { dealId: string | null; status: string };
}

/** Đã nhả mọi căn đang giữ và rời mọi hàng chờ của người nghỉ (idempotent theo '<offboardingId>:units'). */
export interface StaffLocksReleased {
  offboardingId: string;
  idempotencyKey: string; // '<offboardingId>:units'
  staffId: string;
  unitIds: string[];
  dequeuedUnitIds: string[];
}

export interface UnitWithdrawn extends UnitEventBase {
  deletionRequestId: string;
  idempotencyKey: string; // '<deletionRequestId>:withdraw'
  unitId: string;
  projectId: string;
  droppedQueue: string[];
}
export interface UnitWithdrawalRejected {
  deletionRequestId: string;
  idempotencyKey: string;
  unitId: string;
  projectId: string;
  reasonCode: UnitWithdrawalRejectReason;
  message: string;
}
export interface ProjectWithdrawn {
  deletionRequestId: string;
  idempotencyKey: string;
  projectId: string;
  /** Chia lô 1.000. */
  unitIds: string[];
  chunk: { index: number; total: number };
  releasedLocks: { unitId: string; code: string; holderStaffId: string }[];
}
export interface ProjectWithdrawalRejected {
  deletionRequestId: string;
  idempotencyKey: string;
  projectId: string;
  reasonCode: 'PROJECT_HAS_DEALS';
  message: string;
}

export interface EventPayloads {
  [EVENTS.unitLocked]: UnitLocked;
  [EVENTS.unitLockCustomerSet]: UnitLockCustomerSet;
  [EVENTS.unitLockExtended]: UnitLockExtended;
  [EVENTS.unitReleased]: UnitReleased;
  [EVENTS.unitLockExpired]: UnitLockExpired;
  [EVENTS.unitQueueJoined]: UnitQueueJoined;
  [EVENTS.unitQueueLeft]: UnitQueueLeft;
  [EVENTS.unitPriorityWindowClosed]: UnitPriorityWindowClosed;
  [EVENTS.unitBlocked]: UnitBlocked;
  [EVENTS.unitUnblocked]: UnitUnblocked;
  [EVENTS.unitReservedForDeal]: UnitReservedForDeal;
  [EVENTS.unitReservationRejected]: UnitReservationRejected;
  [EVENTS.unitReservationReverted]: UnitReservationReverted;
  [EVENTS.unitDeposited]: UnitDeposited;
  [EVENTS.unitContracted]: UnitContracted;
  [EVENTS.unitReopened]: UnitReopened;
  [EVENTS.unitChangeCompleted]: UnitChangeCompleted;
  [EVENTS.unitChangeRejected]: UnitChangeRejected;
  [EVENTS.unitSyncFailed]: UnitSyncFailed;
  [EVENTS.dealSyncAnomalyDetected]: DealSyncAnomalyDetected;
  [EVENTS.staffLocksReleased]: StaffLocksReleased;
  [EVENTS.unitWithdrawn]: UnitWithdrawn;
  [EVENTS.unitWithdrawalRejected]: UnitWithdrawalRejected;
  [EVENTS.projectWithdrawn]: ProjectWithdrawn;
  [EVENTS.projectWithdrawalRejected]: ProjectWithdrawalRejected;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
